import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGame } from '@/context/GameContext';
import { buildSupabaseHeaders, supabase } from '@/lib/supabase';
import { getValidAccessToken } from '@/lib/auth/session';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import { OAuthProviderIcon } from '@/components/auth/OAuthProviderIcon';
import { COURSES } from '@/data/courses';
import { Module } from '@/types';
import SEO from '@/components/layout/SEO';

const profileSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: 'STARTZONE.DEV // CHARACTER_SHEET',
    description: 'System profile and deployment stats for the current operator.',
});

const BADGE_ARCHIVE = [
    { id: 'git-master', label: 'The Architect', icon: 'military_tech', tone: 'primary' },
    { id: 'configured', label: 'Root User', icon: 'terminal', tone: 'secondary' },
    { id: 'streak-7', label: '24H Sprinter', icon: 'bolt', tone: 'tertiary' },
    { id: 'first-commit', label: 'Security Audit', icon: 'shield', tone: 'primary' },
    { id: 'system-king', label: 'System King', icon: 'lock', tone: 'locked' },
    { id: 'cloud-burst', label: 'Cloud Burst', icon: 'lock', tone: 'locked' },
    { id: 'one-percent', label: 'The 1% Club', icon: 'lock', tone: 'locked' },
    { id: 'byte-lord', label: 'Byte Lord', icon: 'lock', tone: 'locked' },
] as const;

const SYSTEM_NAV = [
    { label: '🏠 Base Camp', path: '/dashboard', icon: 'home' },
    { label: 'Explore', path: '/courses', icon: 'explore' },
    { label: 'Armory', path: '/concepts', icon: 'military_tech' },
    { label: 'System', path: '/profile', icon: 'settings' },
] as const;

function rankLabel(rank: number | null) {
    if (rank !== null && rank <= 10) return 'ELITE';
    if (rank !== null && rank <= 100) return 'VANGUARD';
    return 'OPERATOR';
}

function formatOperatorHandle(handle: string) {
    return handle
        .replace(/\s+/g, '_')
        .replace(/[^A-Za-z0-9_]/g, '_');
}

function formatShortDate(value: string | null | undefined) {
    if (!value) return 'SYNCED';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'SYNCED';
    return date
        .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })
        .replace(/\//g, '.');
}

function SegmentBar({ value, tone }: { value: number; tone: 'primary' | 'secondary' | 'tertiary' }) {
    const palette = {
        primary: 'bg-[#8cacff]',
        secondary: 'bg-[#00cffc]',
        tertiary: 'bg-[#d674ff]',
    } as const;
    const filledSegments = Math.max(1, Math.min(5, Math.round(value / 20)));

    return (
        <div className="flex gap-1 h-1">
            {Array.from({ length: 5 }).map((_, index) => (
                <div
                    key={index}
                    className={`flex-1 ${index < filledSegments ? palette[tone] : 'bg-[#252626]'}`}
                />
            ))}
        </div>
    );
}

export default function ProfilePage() {
    const navigate = useNavigate();
    const { user, signOut } = useAuth();
    const { stats: baseStats, badges, progress, rank, refetch } = useGame();
    const stats = baseStats;

    const [handle, setHandle] = useState('');
    const [savedHandle, setSavedHandle] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (stats?.handle) {
            setHandle(stats.handle);
            setSavedHandle(null);
        }
    }, [stats?.handle]);

    const currentHandle = savedHandle ?? stats?.handle ?? user?.provider_id ?? 'CORE_OPERATOR';
    const operatorHandle = formatOperatorHandle(currentHandle);

    const modules = useMemo(
        () =>
            Object.values(COURSES).flatMap((course) =>
                (course.modules as Module[]).map((module) => ({
                    courseId: course.id,
                    courseTitle: course.title,
                    slug: module.slug,
                    title: module.title,
                    unlock_condition: module.unlock_condition,
                    content: module.content,
                })),
            ),
        [],
    );

    const completedModules = modules.filter((module) => progress[module.slug] === 'completed');
    const totalModules = modules.length;
    const totalXp = stats?.xp ?? 0;
    const level = stats?.level ?? 1;
    const streakDays = stats?.streak_days ?? 0;
    const timeDeployedHours = Math.max(level * 18, completedModules.length * 42, 24);

    const xpStart = Math.max(0, (level - 1) * 500);
    const xpEnd = level * 500;
    const xpProgress = xpEnd > xpStart ? Math.min(100, Math.round(((totalXp - xpStart) / (xpEnd - xpStart)) * 100)) : 0;

    const attributeStats = [
        { label: 'Deployment Logic', value: Math.min(100, 40 + completedModules.length * 7), tone: 'primary' as const },
        { label: 'Neural Architecture', value: Math.min(100, 52 + (badges?.length ?? 0) * 10), tone: 'tertiary' as const },
        { label: 'Terminal Velocity', value: Math.min(100, 34 + level * 3), tone: 'secondary' as const },
    ];

    const deploymentLog = useMemo(() => {
        const completionEntries = completedModules.slice(-2).reverse().map((module, index) => ({
            date: formatShortDate(stats?.last_active),
            title: `Mastered ${module.title}`,
            description: `Completed ${module.courseTitle} objective with full precision. Gained +50 XP.`,
            tone: index === 0 ? 'primary' : 'secondary',
            badge: `${completedModules.length}/${totalModules}`,
            badgeIcon: 'school',
        }));

        const providerEntry = {
            date: formatShortDate(stats?.last_active),
            title: 'System Integration Complete',
            description: `Connected ${user?.provider?.toUpperCase() ?? 'PRIMARY'} uplink and verified identity via provider channel.`,
            tone: 'tertiary',
            badge: 'SUCCESS',
            badgeIcon: 'link',
        };

        return [...completionEntries, providerEntry].slice(0, 3);
    }, [completedModules, stats?.last_active, totalModules, user?.provider]);

    const linkedUplinks = [
        { provider: 'github', label: 'GitHub' },
        { provider: 'google', label: 'Google' },
        { provider: 'discord', label: 'Discord' },
    ] as const;

    const invokeHandleUpdate = async (newHandle: string | null) => {
        if (!supabase) {
            throw new Error('Connection error');
        }

        const accessToken = await getValidAccessToken();
        if (!accessToken) {
            throw new Error('Session expired. Please sign in again.');
        }

        const { data: responseData, error: invokeError } = await supabase.functions.invoke('update-handle', {
            body: { newHandle },
            headers: buildSupabaseHeaders(accessToken),
        });

        if (invokeError) throw invokeError;
        if (responseData && typeof responseData === 'object' && 'error' in responseData && responseData.error) {
            throw new Error(typeof responseData.error === 'string' ? responseData.error : 'Edge Function returned an error.');
        }
    };

    const handleSave = async () => {
        const trimmed = handle.trim();
        if (!trimmed || trimmed === stats?.handle) {
            setIsEditing(false);
            return;
        }

        if (trimmed.length < 3 || trimmed.length > 20 || !/^[a-zA-Z0-9_]+$/.test(trimmed)) {
            setError('Handle must be 3-20 characters (letters, numbers, underscores only).');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await invokeHandleUpdate(trimmed);
            setSavedHandle(trimmed);
            setIsEditing(false);
            if (refetch) void refetch().catch(() => {});
        } catch (e: any) {
            let msg = e?.message ?? 'Network error — please try again.';
            const context = e?.context;
            if (context) {
                try {
                    const clone = context.clone ? context.clone() : context;
                    const body = await clone.json();
                    if (body?.error) msg = body.error;
                } catch {
                    try {
                        const text = await (context.text?.() ?? Promise.resolve(''));
                        if (text) msg = text;
                    } catch {
                        // ignore
                    }
                }
            }
            setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-[#0e0e0e] px-6 py-24 text-white">
                <SEO schema={profileSchema} title="STARTZONE.DEV // CHARACTER_SHEET" />
                <div className="mx-auto flex max-w-3xl flex-col items-center justify-center text-center">
                    <div className="mb-10 flex h-32 w-32 items-center justify-center rounded-full border border-white/10 bg-[#131313] text-5xl opacity-60">
                        <MaterialIcon icon="account_circle" size={72} className="text-[#8cacff]" />
                    </div>
                    <h1 className="mb-6 font-['Space_Grotesk'] text-5xl font-black italic uppercase tracking-tighter">Identity Required</h1>
                    <p className="mb-12 max-w-md text-lg leading-relaxed text-[#acabaa]">
                        You&apos;re currently browsing the server as a ghost. Sign in to access your character sheet and system telemetry.
                    </p>
                    <Link
                        to="/"
                        className="bg-gradient-to-r from-[#8cacff] to-[#769dff] px-10 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-[#001f55] transition-all hover:brightness-110 active:scale-95"
                    >
                        Join The Server
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0e0e0e] text-white">
            <SEO schema={profileSchema} title="STARTZONE.DEV // CHARACTER_SHEET" />

            <aside className="fixed left-0 top-20 z-40 hidden h-[calc(100%-5rem)] w-64 flex-col border-r border-[#484848]/15 bg-[#0e0e0e] pb-8 pt-4 md:flex">
                <div className="mb-10 px-6">
                    <div className="mb-2 flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center border border-[#00cffc]/30 bg-[#252626]">
                            <MaterialIcon icon="terminal" size={24} fill className="text-[#00cffc]" />
                        </div>
                        <div>
                            <h3 className="font-['Space_Grotesk'] text-sm font-black uppercase tracking-tighter text-[#8cacff]">
                                LEVEL {level}
                            </h3>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-[#acabaa]">ELITE DEPLOYER</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="w-full border border-[#8cacff]/30 bg-[#8cacff]/10 py-2 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.2em] text-[#8cacff] transition-all hover:bg-[#8cacff]/20 active:translate-x-1"
                    >
                        REDEEM XP
                    </button>
                </div>

                <nav className="flex-1 space-y-1">
                    {SYSTEM_NAV.map((item) => (
                        <Link
                            key={item.label}
                            to={item.path}
                            className={item.path === '/profile'
                                ? "flex items-center gap-4 border-l-4 border-[#00cffc] bg-[#131313] py-3 pl-6 font-['Space_Grotesk'] text-sm font-bold uppercase text-[#00cffc] active:translate-x-1"
                                : "flex items-center gap-4 py-3 pl-6 font-['Space_Grotesk'] text-sm font-bold uppercase text-[#484848] transition-colors hover:bg-[#131313] hover:text-[#8cacff]"}
                        >
                            <MaterialIcon icon={item.icon} size={20} className="text-current" />
                            {item.label}
                        </Link>
                    ))}
                </nav>
            </aside>

            <main className="relative min-h-[calc(100vh-5rem)] overflow-hidden bg-[#0e0e0e] px-4 pb-24 pt-8 md:ml-64 md:px-12">
                <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{ background: 'linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.1) 50%)', backgroundSize: '100% 4px' }} />
                <div className="pointer-events-none absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(#8cacff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                <div className="relative z-10 mb-12 flex flex-col items-end gap-8 md:flex-row">
                    <div className="group relative">
                        <div className="absolute -inset-1 bg-gradient-to-tr from-[#8cacff] to-[#00cffc] opacity-25 blur-lg transition-opacity group-hover:opacity-40" />
                        <div className="relative flex h-40 w-40 items-center justify-center border-2 border-[#484848] bg-[#131313] p-1">
                            <MaterialIcon icon="account_circle" size={124} className="text-[#8cacff]" />
                            <div className="absolute bottom-0 right-0 bg-[#8cacff] px-2 py-0.5 font-['Space_Grotesk'] text-[10px] font-black text-[#002a6d]">
                                {user.provider_id.slice(0, 8)}_ID
                            </div>
                        </div>
                    </div>

                    <div className="w-full flex-1">
                        <div className="mb-2 flex flex-wrap items-end gap-4">
                            <h1 className="font-['Space_Grotesk'] text-5xl font-black uppercase tracking-tighter italic text-white">
                                {operatorHandle}
                            </h1>
                            <span className="font-['Inter'] text-sm tracking-widest text-[#8cacff]/50">[STATUS: ACTIVE]</span>
                            <Link
                                to="/profile/settings"
                                className="font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.25em] text-[#8cacff]/60 transition-colors hover:text-[#00cffc]"
                            >
                                System Config
                            </Link>
                            {!isEditing ? (
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(true)}
                                    className="font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.25em] text-[#8cacff]/60 transition-colors hover:text-[#00cffc]"
                                >
                                    Edit
                                </button>
                            ) : null}
                            <button
                                type="button"
                                onClick={() => signOut()}
                                className="font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.25em] text-[#ff716c]/70 transition-colors hover:text-[#ff716c]"
                            >
                                Sign Out
                            </button>
                        </div>

                        {isEditing ? (
                            <div className="mb-4 w-full max-w-md bg-[#131313] p-3">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={handle}
                                        onChange={(event) => setHandle(event.target.value)}
                                        className="w-full border border-[#484848]/30 bg-[#1f2020] px-3 py-2 font-['Inter'] text-sm text-white outline-none"
                                        maxLength={20}
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={isLoading || handle === stats?.handle}
                                        className="bg-[#8cacff] px-4 py-2 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.2em] text-[#001f55] disabled:opacity-40"
                                    >
                                        {isLoading ? 'Saving…' : 'Save'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditing(false);
                                            setHandle(stats?.handle || '');
                                            setError(null);
                                        }}
                                        disabled={isLoading}
                                        className="border border-[#484848]/30 px-4 py-2 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.2em] text-[#acabaa] disabled:opacity-40"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <p className="mt-2 text-xs text-[#acabaa]">Use letters, numbers, and underscores only.</p>
                                {error ? <p className="mt-2 text-xs text-[#ff716c]">{error}</p> : null}
                            </div>
                        ) : null}

                        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                            <div className="border-l-2 border-[#00cffc]/50 bg-[#131313] p-3">
                                <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-[#acabaa]">TOTAL XP</p>
                                <p className="font-['Space_Grotesk'] text-2xl font-bold text-[#00cffc]">{totalXp.toLocaleString()}</p>
                            </div>
                            <div className="border-l-2 border-[#8cacff]/50 bg-[#131313] p-3">
                                <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-[#acabaa]">TIME DEPLOYED</p>
                                <p className="font-['Space_Grotesk'] text-2xl font-bold text-[#8cacff]">{timeDeployedHours}H</p>
                            </div>
                            <div className="border-l-2 border-[#d674ff]/50 bg-[#131313] p-3">
                                <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-[#acabaa]">STREAK</p>
                                <p className="font-['Space_Grotesk'] text-2xl font-bold text-[#d674ff]">{streakDays} DAYS</p>
                            </div>
                            <div className="border-l-2 border-white/50 bg-[#131313] p-3">
                                <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-[#acabaa]">RANK</p>
                                <p className="font-['Space_Grotesk'] text-2xl font-bold text-white">{rankLabel(rank)}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative z-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="space-y-6 lg:col-span-1">
                        <div className="relative overflow-hidden border border-[#484848] bg-[#131313] p-6">
                            <div className="absolute -mr-16 -mt-16 h-32 w-32 rounded-full bg-[#8cacff]/5 blur-3xl right-0 top-0" />
                            <div className="mb-6 flex items-end justify-between">
                                <div>
                                    <h2 className="font-['Space_Grotesk'] text-3xl font-black italic tracking-tighter">LVL {level}</h2>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#00cffc]">MASTER ARCHITECT</p>
                                </div>
                                <p className="font-['Inter'] text-xs text-[#acabaa]">{xpProgress}% TO LVL {level + 1}</p>
                            </div>
                            <div className="relative mb-2 h-4 w-full bg-[#252626]">
                                <div className="relative h-full bg-gradient-to-r from-[#8cacff] to-[#00cffc]" style={{ width: `${xpProgress}%` }}>
                                    <div className="absolute inset-0 bg-white/10 mix-blend-overlay" />
                                </div>
                            </div>
                            <div className="flex justify-between font-['Space_Grotesk'] text-[10px] font-black text-[#acabaa]">
                                <span>{xpStart.toLocaleString()} XP</span>
                                <span>{xpEnd.toLocaleString()} XP</span>
                            </div>
                        </div>

                        <div className="border border-[#484848] bg-[#131313] p-6">
                            <h3 className="mb-6 flex items-center gap-2 font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.2em]">
                                <span className="h-2 w-2 bg-[#00cffc]" />
                                CORE_ATTRIBUTES
                            </h3>
                            <div className="space-y-6">
                                {attributeStats.map((attribute) => (
                                    <div key={attribute.label}>
                                        <div className="mb-2 flex justify-between">
                                            <span className="text-xs font-bold uppercase tracking-widest">{attribute.label}</span>
                                            <span className={`font-['Inter'] text-xs ${attribute.tone === 'primary' ? 'text-[#8cacff]' : attribute.tone === 'secondary' ? 'text-[#00cffc]' : 'text-[#d674ff]'}`}>
                                                {attribute.value}/100
                                            </span>
                                        </div>
                                        <SegmentBar value={attribute.value} tone={attribute.tone} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border border-[#484848] bg-[#131313] p-6">
                            <h3 className="mb-6 font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.2em]">LINKED_UPLINKS</h3>
                            <div className="space-y-3">
                                {linkedUplinks.map((uplink) => (
                                    <div key={uplink.provider} className="group flex items-center justify-between border border-[#484848]/30 bg-[#1f2020] p-3 transition-colors hover:border-[#8cacff]/50">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded bg-white/5">
                                                <OAuthProviderIcon provider={uplink.provider} className="h-5 w-5 text-white" />
                                            </div>
                                            <span className="text-xs font-bold uppercase tracking-wider">{uplink.label}</span>
                                        </div>
                                        <span className="text-[10px] font-black uppercase text-[#00cffc]">
                                            {uplink.provider === user.provider ? 'CONNECTED' : 'AVAILABLE'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6 lg:col-span-2">
                        <div className="relative border border-[#484848] bg-[#131313] p-8">
                            <div className="absolute right-4 top-4 flex gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-[#00cffc] shadow-[0_0_8px_#00cffc]" />
                                <div className="h-1.5 w-1.5 rounded-full bg-[#00cffc]/30" />
                                <div className="h-1.5 w-1.5 rounded-full bg-[#00cffc]/30" />
                            </div>
                            <h3 className="mb-8 font-['Space_Grotesk'] text-lg font-black uppercase tracking-widest italic">
                                BADGE_COLLECTION // ARCHIVED
                            </h3>
                            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4">
                                {BADGE_ARCHIVE.map((badgeSpec, index) => {
                                    const earned = badges?.includes(badgeSpec.id) || index < 4;
                                    const toneClass =
                                        badgeSpec.tone === 'secondary'
                                            ? 'text-[#00cffc] bg-[#00cffc]/20'
                                            : badgeSpec.tone === 'tertiary'
                                                ? 'text-[#d674ff] bg-[#d674ff]/20'
                                                : 'text-[#8cacff] bg-[#8cacff]/20';

                                    if (!earned) {
                                        return (
                                            <div key={badgeSpec.id} className="aspect-square border border-[#484848]/10 bg-[#252626]/30 p-4 opacity-30 grayscale blur-[1px]">
                                                <div className="flex h-full flex-col items-center justify-center">
                                                    <div className="mb-3 flex h-16 w-16 items-center justify-center">
                                                        <MaterialIcon icon="lock" size={32} className="text-white" />
                                                    </div>
                                                    <span className="text-center text-[10px] font-black uppercase tracking-tighter leading-none">
                                                        {badgeSpec.label}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={badgeSpec.id} className="group relative aspect-square cursor-help border border-[#484848] bg-[#1f2020] p-4 transition-all hover:border-[#8cacff]/50">
                                            <div className="flex h-full flex-col items-center justify-center">
                                                <div className={`mb-3 flex h-16 w-16 items-center justify-center rounded-lg ${toneClass} transition-transform group-hover:scale-110`}>
                                                    <MaterialIcon icon={badgeSpec.icon} size={32} fill className={badgeSpec.tone === 'secondary' ? 'text-[#00cffc]' : badgeSpec.tone === 'tertiary' ? 'text-[#d674ff]' : 'text-[#8cacff]'} />
                                                </div>
                                                <span className="text-center text-[10px] font-black uppercase tracking-tighter leading-none">
                                                    {badgeSpec.label}
                                                </span>
                                                {badgeSpec.tone === 'primary' ? <div className="absolute inset-0 bg-[#8cacff]/5 opacity-0 transition-opacity group-hover:opacity-100" /> : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="border border-[#484848] bg-[#131313] p-8">
                            <div className="mb-8 flex items-center justify-between">
                                <h3 className="font-['Space_Grotesk'] text-lg font-black uppercase tracking-widest italic">
                                    DEPLOYMENT_LOG
                                </h3>
                                <span className="font-['Inter'] text-[10px] text-[#acabaa]">REC_ID: 88219-X</span>
                            </div>
                            <div className="space-y-4">
                                {deploymentLog.map((entry) => {
                                    const tone =
                                        entry.tone === 'secondary'
                                            ? {
                                                border: 'border-[#00cffc]',
                                                text: 'text-[#00cffc]',
                                                bg: 'bg-[#00cffc]/10',
                                            }
                                            : entry.tone === 'tertiary'
                                                ? {
                                                    border: 'border-[#d674ff]',
                                                    text: 'text-[#d674ff]',
                                                    bg: 'bg-[#d674ff]/10',
                                                }
                                                : {
                                                    border: 'border-[#8cacff]',
                                                    text: 'text-[#8cacff]',
                                                    bg: 'bg-[#8cacff]/10',
                                                };

                                    return (
                                        <div key={`${entry.date}-${entry.title}`} className={`flex items-start gap-6 border-l-2 ${tone.border} bg-[#1f2020] p-4`}>
                                            <span className={`mt-1 font-['Inter'] text-xs ${tone.text}`}>{entry.date}</span>
                                            <div className="flex-1">
                                                <h4 className="mb-1 text-xs font-black uppercase tracking-wider">{entry.title}</h4>
                                                <p className="text-[10px] leading-relaxed text-[#acabaa]">{entry.description}</p>
                                            </div>
                                            <div className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase ${tone.text} ${tone.bg}`}>
                                                <MaterialIcon icon={entry.badgeIcon} size={14} className="text-current" />
                                                {entry.badge}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pointer-events-none fixed bottom-24 right-12 hidden text-right opacity-20 xl:block">
                    <div className="space-y-1 font-['Inter'] text-[10px] tracking-widest text-[#00cffc]">
                        <p>UPLINK_STRENGTH: 98%</p>
                        <p>LATENCY: 14MS</p>
                        <p>DATA_STREAM: ENCRYPTED</p>
                        <p>LOCAL_TIME: 03:42:01</p>
                    </div>
                </div>
            </main>

            <footer className="fixed bottom-0 left-0 z-50 flex h-16 w-full items-center justify-around border-t border-[#484848]/20 bg-[#131313]/90 shadow-[0_-4px_24px_rgba(0,0,0,0.8)] backdrop-blur-xl md:hidden">
                <Link to="/" className="flex flex-col items-center justify-center font-['Inter'] text-[10px] font-black uppercase tracking-widest text-[#484848] transition-transform hover:text-[#8cacff] active:scale-90">
                    <MaterialIcon icon="home" size={20} className="text-current" />
                    <span>Home</span>
                </Link>
                <Link to="/courses" className="flex flex-col items-center justify-center font-['Inter'] text-[10px] font-black uppercase tracking-widest text-[#484848] transition-transform hover:text-[#8cacff] active:scale-90">
                    <MaterialIcon icon="school" size={20} className="text-current" />
                    <span>Courses</span>
                </Link>
                <Link to="/profile" className="flex flex-col items-center justify-center font-['Inter'] text-[10px] font-black uppercase tracking-widest text-[#00cffc] drop-shadow-[0_0_8px_rgba(0,207,252,0.5)] transition-transform active:scale-90">
                    <MaterialIcon icon="skeleton" size={20} className="text-current" />
                    <span>Profile</span>
                </Link>
                <button
                    type="button"
                    onClick={() => navigate('/profile')}
                    className="flex flex-col items-center justify-center font-['Inter'] text-[10px] font-black uppercase tracking-widest text-[#484848] transition-transform hover:text-[#8cacff] active:scale-90"
                >
                    <MaterialIcon icon="settings" size={20} className="text-current" />
                    <span>Settings</span>
                </button>
            </footer>
        </div>
    );
}
